import { detectWorkbenchCapability } from '../webcontainer/capability';
import { isWorkbenchPerfEnabled } from './workbenchPerf';

/**
 * Memory telemetry at the moments the review flagged as peaks (PROTOTYPER_STABILITY_TECH_PLAN.md
 * P0.3): WebContainer boot, dependencies mounted, first rendered preview, and each build.
 *
 * Sent as a `workbench_memory` event through the `ideoz-hot-edit-analytics` window event, which
 * `WorkbenchPanel` (packages/web) already forwards to `trackEvent`. `bytes` comes from
 * `performance.measureUserAgentSpecificMemory()` (Chromium, cross-origin isolated pages; it can
 * take seconds because it waits for garbage collection) and is `null` elsewhere.
 */

export type WorkbenchMemoryStage = 'boot' | 'deps-mounted' | 'first-preview' | 'build';

export const WORKBENCH_ANALYTICS_EVENT = 'ideoz-hot-edit-analytics';

type MemoryPerformance = Performance & {
  measureUserAgentSpecificMemory?: () => Promise<{ bytes: number }>;
  memory?: { usedJSHeapSize?: number };
};

const recordedOnce = new Set<WorkbenchMemoryStage>();
let measurementInFlight = false;

function emit(properties: Record<string, unknown>): void {
  try {
    window.dispatchEvent(
      new CustomEvent(WORKBENCH_ANALYTICS_EVENT, {
        detail: { eventName: 'workbench_memory', properties },
      })
    );
  } catch {
    /* analytics must never break the workbench */
  }
  if (isWorkbenchPerfEnabled()) console.info('[workbench] memory', properties);
}

/** `once`: report this stage only the first time in the page's life (e.g. first preview). */
export function recordWorkbenchMemory(
  stage: WorkbenchMemoryStage,
  opts?: { once?: boolean; extra?: Record<string, unknown> }
): void {
  if (typeof window === 'undefined') return;
  if (opts?.once) {
    if (recordedOnce.has(stage)) return;
    recordedOnce.add(stage);
  }

  const perf = globalThis.performance as MemoryPerformance | undefined;
  const nav = globalThis.navigator as (Navigator & { deviceMemory?: number }) | undefined;
  const base: Record<string, unknown> = {
    stage,
    deviceMemoryGb: typeof nav?.deviceMemory === 'number' ? nav.deviceMemory : null,
    tier: detectWorkbenchCapability().tier,
    jsHeapBytes: perf?.memory?.usedJSHeapSize ?? null,
    ...opts?.extra,
  };

  const canMeasure =
    Boolean(globalThis.crossOriginIsolated) &&
    typeof perf?.measureUserAgentSpecificMemory === 'function';
  // One measurement at a time: overlapping calls would each wait for the same GC.
  if (!canMeasure || measurementInFlight) {
    emit({ ...base, bytes: null });
    return;
  }

  measurementInFlight = true;
  perf!.measureUserAgentSpecificMemory!()
    .then((result) => emit({ ...base, bytes: result.bytes }))
    .catch(() => emit({ ...base, bytes: null }))
    .finally(() => {
      measurementInFlight = false;
    });
}
